<script setup lang="ts">
import IconAtom from "./IconAtom.vue";

defineProps<{
  label?: string;
  variant?: "primary" | "destructive" | "outline" | "ghost" | "soft-neutral";
  size?: "sm" | "base" | "lg";
  iconLeading?: string;
  iconTrailing?: string;
  iconSrc?: string;
  iconStyle?: Record<string, string>;
  disabled?: boolean;
  loading?: boolean;
  href?: string;
  target?: string;
}>();

const emit = defineEmits<{
  (e: "click", event: MouseEvent): void;
}>();
</script>

<template>
  <component
    :is="href ? 'a' : 'button'"
    class="button-atom"
    :class="[
      `button-atom--${variant || 'primary'}`,
      `button-atom--${size || 'base'}`,
      { 'button-atom--disabled': disabled || loading },
      { 'button-atom--loading': loading },
    ]"
    :disabled="href ? undefined : disabled || loading"
    :href="href"
    :target="target"
    :rel="target === '_blank' ? 'noopener noreferrer' : undefined"
    @click="emit('click', $event)"
  >
    <span v-if="loading" class="button-atom__spinner" aria-hidden="true" />
    <img
      v-if="iconSrc && !loading"
      :src="iconSrc"
      alt=""
      :style="{
        width: size === 'sm' ? '20px' : size === 'lg' ? '24px' : '20px',
        height: size === 'sm' ? '20px' : size === 'lg' ? '24px' : '20px',
        ...iconStyle,
      }"
      class="button-atom__icon-leading"
    />
    <IconAtom
      v-else-if="iconLeading && !loading"
      :name="iconLeading as any"
      :size="size === 'sm' ? 20 : size === 'lg' ? 24 : 20"
      class="button-atom__icon-leading"
    />
    <span v-if="label" class="button-atom__label">{{ label }}</span>
    <IconAtom
      v-if="iconTrailing && !loading"
      :name="iconTrailing as any"
      :size="size === 'sm' ? 20 : size === 'lg' ? 24 : 20"
      class="button-atom__icon-trailing"
    />
  </component>
</template>

<style scoped lang="scss">
@use "@/assets/styles/design-tokens.scss" as *;

.button-atom {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: $spacing-1-5;
  border: none;
  border-radius: 12px;
  font-family: "Satoshi", sans-serif;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  text-decoration: none;

  &:focus-visible {
    outline: 2px solid #5e58ee;
    outline-offset: 2px;
  }

  &--destructive:focus-visible {
    outline-color: $color-button-destructive;
  }

  // Sizes
  &--sm {
    height: 28px;
    padding: $spacing-1-5 $spacing-2;
    /* stylelint-disable-next-line declaration-property-value-disallowed-list -- missing weight, cannot tier */
    font-size: 12px;
    line-height: $txt-xsmall-line-height;
    letter-spacing: 0.3px;
  }

  &--base {
    height: 32px;
    padding: $spacing-1-5 $spacing-2;
    /* stylelint-disable-next-line declaration-property-value-disallowed-list -- missing weight, cannot tier */
    font-size: 14px;
    line-height: $txt-small-line-height;
    letter-spacing: 0.3px;
  }

  &--lg {
    height: 40px;
    padding: $spacing-2 $spacing-3;
    /* stylelint-disable-next-line declaration-property-value-disallowed-list -- missing weight, cannot tier */
    font-size: 14px;
    line-height: $txt-small-line-height;
    letter-spacing: 0.3px;
    border-radius: 16px;
  }

  // Variants
  &--primary {
    background-color: #5e58ee;
    color: #ffffff;

    &:hover:not(.button-atom--disabled) {
      background-color: #4b47d1;
    }

    &:active:not(.button-atom--disabled) {
      background-color: #3a37b8;
    }
  }

  &--destructive {
    background-color: $color-button-destructive;
    color: #ffffff;

    &:hover:not(.button-atom--disabled) {
      background-color: $color-button-destructive-hover;
    }

    &:active:not(.button-atom--disabled) {
      background-color: $color-button-destructive-active;
    }
  }

  &--outline {
    background-color: transparent;
    border: 1px solid #c6ccdb;
    color: #121826;

    &:hover:not(.button-atom--disabled) {
      background-color: #f9fafb;
      border-color: #8896aa;
    }

    &:active:not(.button-atom--disabled) {
      background-color: #f4f5f8;
    }

    /* Outline Disable (Figma design system) */
    &.button-atom--disabled {
      opacity: 1;
      background-color: #f6f7f9;
      border-color: #e7eaee;
      color: #a6b0bf;
    }
  }

  &--ghost {
    background-color: transparent;
    color: #646e87;
    border: 1px solid transparent;

    &:hover:not(.button-atom--disabled) {
      background-color: #f9fafb;
      border-color: #dfe3ea;
    }

    &:active:not(.button-atom--disabled) {
      background-color: #f4f5f8;
    }
  }

  &--soft-neutral {
    background-color: #eaecf1;
    color: #121826;
    border: 1px solid #dfe3ea;

    &:hover:not(.button-atom--disabled) {
      background-color: #dfe3ea;
    }

    &:active:not(.button-atom--disabled) {
      background-color: #c6ccdb;
    }
  }

  // Disabled state
  &--disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  // Icon styles
  &__icon-leading,
  &__icon-trailing {
    flex-shrink: 0;
  }

  &__label {
    font-weight: inherit;
  }

  // Loading spinner inside button (min size so it stays visible)
  &__spinner {
    width: 1.25em;
    min-width: 18px;
    height: 1.25em;
    min-height: 18px;
    border: 2px solid currentColor;
    border-right-color: transparent;
    border-radius: 50%;
    animation: button-atom-spin 0.75s linear infinite;
    flex-shrink: 0;
    display: inline-block;
    vertical-align: middle;
  }
}

@keyframes button-atom-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
